/**
 * KAN-619: ручной дневной лимит первых сообщений аккаунта, инлайн-редактор
 * в ячейке "Отправлено сегодня". Клик по лимиту открывает поле, Enter и blur
 * сохраняют, Escape отменяет. Пустое значение возвращает общий лимит канала.
 */
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isLimitReached } from '@/lib/telegramAccountFormat';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const QUERY_KEY = ['telegram-accounts'];
const MIN_LIMIT = 0;
const MAX_LIMIT = 50;
const RISKY_ABOVE = 10;
const OUT_OF_RANGE = `Целое число от ${MIN_LIMIT} до ${MAX_LIMIT}, пусто: общий лимит`;
const SAVE_ERROR = 'Не удалось сохранить дневной лимит';
const RISK_WARNING =
  'Больше 10 в день для свежего аккаунта рискованно: возможна блокировка Telegram';
const APPLIES_LATER = 'применится после перезапуска контейнера';

/** undefined: значение недопустимо; null: пусто (общий лимит канала). */
function parseLimit(raw: string): number | null | undefined {
  const text = raw.trim();
  if (text === '') return null;
  if (!/^\d+$/.test(text)) return undefined;
  const value = Number(text);
  return value >= MIN_LIMIT && value <= MAX_LIMIT ? value : undefined;
}

export function DailyLimitEditor({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element {
  const queryClient = useQueryClient();
  const key = `${account.operator_id}-${account.account_id}`;
  const current = account.daily_limit_override ?? null;
  const currentText = current === null ? '' : String(current);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentText);
  const [error, setError] = useState(false);
  const done = useRef(false);

  const save = useMutation({
    mutationFn: (daily_limit: number | null) =>
      telegramAccountsApi.updateAccount(
        account.account_id,
        { daily_limit },
        account.operator_id
      ),
    onMutate: async (daily_limit) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const prev = queryClient.getQueryData<TelegramAccount[]>(QUERY_KEY);
      queryClient.setQueryData<TelegramAccount[] | undefined>(QUERY_KEY, (list) =>
        list?.map((a) => {
          if (
            a.operator_id !== account.operator_id ||
            a.account_id !== account.account_id
          ) {
            return a;
          }
          // Эффективный лимит меняем сами, только если свой лимит действует сразу;
          // общий лимит канала после очистки придёт с сервера.
          const applies = daily_limit !== null && a.daily_limit_applies !== false;
          return {
            ...a,
            daily_limit_override: daily_limit,
            daily_limit: applies ? daily_limit : a.daily_limit,
          };
        })
      );
      return { prev };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev);
      toast.error(SAVE_ERROR);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const start = (): void => {
    done.current = false;
    setDraft(currentText);
    setError(false);
    setEditing(true);
  };

  const cancel = (): void => {
    done.current = true;
    setError(false);
    setEditing(false);
  };

  const commit = (): void => {
    if (done.current) return;
    const value = parseLimit(draft);
    if (value === undefined) {
      setError(true);
      return;
    }
    done.current = true;
    setError(false);
    setEditing(false);
    if (value !== current) save.mutate(value);
  };

  const limited = isLimitReached(account.sent_today, account.daily_limit);
  const pending = current !== null && account.daily_limit_applies === false;
  const draftValue = parseLimit(draft);
  const risky = editing
    ? typeof draftValue === 'number' && draftValue > RISKY_ABOVE
    : current !== null && current > RISKY_ABOVE;
  // Свой лимит ещё не действует: эффективный отличается, показываем оба числа
  const ownLabel =
    current !== null && current !== account.daily_limit ? `свой: ${current}` : 'свой';

  return (
    <div className="flex flex-col items-start gap-0.5">
      <span
        data-testid={`sent-today-${key}`}
        data-limit-reached={limited ? 'true' : 'false'}
        className={
          limited
            ? 'rounded bg-red-100 px-2 py-0.5 text-sm font-semibold text-red-700'
            : 'text-sm text-gray-700'
        }
      >
        {account.sent_today} /{' '}
        {editing ? (
          <input
            autoFocus
            type="text"
            inputMode="numeric"
            value={draft}
            placeholder="общий"
            aria-label="Дневной лимит первых сообщений"
            aria-invalid={error}
            data-testid={`daily-limit-input-${key}`}
            className="h-7 w-16 rounded border px-2 text-xs font-normal text-gray-900"
            onChange={(e) => {
              setDraft(e.target.value);
              setError(false);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') cancel();
            }}
          />
        ) : (
          <button
            type="button"
            data-testid={`daily-limit-${key}`}
            title="Дневной лимит первых сообщений: нажмите, чтобы изменить"
            className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
            onClick={start}
          >
            {account.daily_limit}
          </button>
        )}
      </span>
      {error && (
        <span
          role="alert"
          data-testid={`daily-limit-error-${key}`}
          className="text-xs text-red-700"
        >
          {OUT_OF_RANGE}
        </span>
      )}
      <span className="text-xs text-gray-500">
        {current === null ? 'общий' : ownLabel}
      </span>
      {pending && (
        <span className="text-xs italic text-gray-500">{APPLIES_LATER}</span>
      )}
      {risky && (
        <span className="max-w-[200px] text-xs text-amber-700">{RISK_WARNING}</span>
      )}
    </div>
  );
}
